/* eslint-disable no-debugger */

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useDashboardContext } from "../../contexts/dashboard-context";
import { useImporter } from "../../contexts/importer-context";
import { usePreviewContext } from "../../contexts/preview-context";
import { usePhotoPreprocessProgress } from "../../hooks/use-photo-preprocess-progress";
import { useAppContext } from "../../hooks/useAppContext";
import { useBottomMenu } from "../../hooks/useBottomMenu";
import { useEditor } from "../../hooks/useEditor";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useFeatureFlagToggle } from "../../hooks/useFeatureFlags";
import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { useIsTextEditMode } from "../../hooks/useIsTextEditMode";
import { useIsTransformMode } from "../../hooks/useIsTransformMode";
import { useLabels } from "../../hooks/useLabels";
import { useMessage } from "../../hooks/useMessage";
import { usePreviewBox } from "../../hooks/usePreviewBox";
import {
	useLogicalSelectedPage,
	useSelectedSpread,
	useSelectedZone,
} from "../../hooks/useSelection";
import { useSmartFilterLoader } from "../../hooks/useSmartFilterLoader";
import { BottomMenuIds, BottomMenuPaths } from "../bottom-menu/paths";
import { isPhotoTransformSupported } from "../bottom-photo-menus/photo-transform-panel/menu-info";
import { DragImage, IDragImageRef } from "../drag-image";
import { GuidelineOverlay, IGuidelineOverlayRef } from "../guideline-overlay";
import { useGuidelineOverlay } from "../guideline-overlay/use-guideline-overlay";
import { useImportPhotosDialog } from "../import-photos-button";
import { PanCropOverlay } from "../pan-crop-overlay.ts";
import { PreviewOverlay } from "../preview-overlay";
import {
	IZoneSelectionBoxRef,
	ZoneSelectionBox,
	ZoneSelectionBoxHandlers,
} from "../zone-selection-box";
import { updateBottomMenuWhenClicked } from "./bottom-menu-logic";
import { useAdornerImageCreator } from "./hooks/use-adorner-image-creator";
import { labels } from "./labels";
import { SlideshowPreview } from "./slideshow-preview";
import { createSpreadSlideShowItem, ISpreadSlideShowItem } from "./spread-slideshow-item";
import { useSpreadDirtyChecker } from "./use-dirty-checker";
import { usePreviewOverlay } from "./use-preview-overlay";
import { useBindSlideshowGestures } from "./use-slideshow-gestures";
import { useZoneSelectionBox } from "./use-zone-selection-box";
import { useSpreadUnloadingPreloading } from "./useSpreadUnloadingPreloading";
import { jsonComparer, useMessageToState } from "../../hooks/useMessageToState";
import { SpreadOverview } from "../spread-overview";
import { usePhotoSwapping } from "./hooks/use-photo-swapping";
import { useZoneMove } from "./hooks/use-zone-move";
import { usePhotoPanning } from "./hooks/use-photo-panning";
import { getDefaultPathForZone } from "../bottom-menu/bottom-menu-logic";
import { useMapZones } from "../bottom-menus/bottom-root-menu/use-map-zones";

// ╔═════════════════════════════════════════════╗
// ║       Slideshow-Preview-Container           ║
// ╚════════╤═══════════════════════╤════════════╝
//          ↓ Create slideshow      ↓ Create / update spread items
// ╔════════════════════╗  ╔═══════════════════════════════╗
// ║ Slideshow          ║  ║ ┌───────────────────────┐     ║
// ╚════════════════════╝  ║ │  SpreadSlideShowItem  │ ┐   ║
//                         ║ └───────────────────────┘ │ ┐ ║
//                         ║   └───────────────────────┘ │ ║
//                         ║     └───────────────────────┘ ║
//                         ╚═══════════════════════════════╝
//
// ------------
// INIT SPREADS:
// ------------
// ╔═════════════════════════╗
// ║ Core: Broadcaster       ║
// ╚═══╤═════════════════════╝
//     │ 1) onMessage (new spreads)
//     ↓
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║          Slideshow-Preview-Container                                      ║
// ╚═══╤══════════════════════════════════════════════╤════════════════════════╝
//     ↓         ↑ 4) onViewChanged                   ↓
// ╔═════════════╧════════════════╗      ╔═════════════════════════════════════╗
// ║ Slideshow                    ║      ║ Spread-slideshow                    ║
// ║                              ║      ║                                     ║
// ║ 3) set items                 ║      ║ 2) create items                     ║
// ║                              ║      ║ ┌─────────────────────────────┐     ║    ┌───────────────────────┐        ┌───────────────────────┐
// ╚══════════════════════════════╝      ║ │ 5) updatePreviewAsync       │ ┐ ──║───→│ renderers             │ ┐ ────→│ DOM elements          │ ┐
//                                       ║ └─────────────────────────────┘ │ ┐ ║    └───────────────────────┘ │ ┐    └───────────────────────┘ │ ┐
//                                       ║   └─────────────────────────────┘ │ ║      └───────────────────────┘ │      └───────────────────────┘ │
//                                       ║     └─────────────────────────────┘ ║        └───────────────────────┘        └───────────────────────┘
//                                       ╚═════════════════════════════════════╝
//
// ---------------
// RENDER PREVIEW:
// ---------------
// ╔═════════════════════════╗
// ║ Core: Broadcaster       ║
// ╚═══╤═════════════════════╝
//     │ 1) onMessage (Spread updated)
//     ↓
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║          Slideshow-Preview-Container                                      ║
// ╚═══╤══════════════════════════════════════════════╤════════════════════════╝
//     ↓         ↑ 4) onItemTransformChanged          ↓
// ╔═════════════╧════════════════╗      ╔═════════════════════════════════════╗
// ║ Slideshow                    ║      ║ Spread-slideshow                    ║
// ║                              ║      ║                                     ║
// ║                              ║      ║ ┌─────────────────────────────┐     ║    ┌───────────────────────┐        ┌───────────────────────┐
// ║ 3) setItemSize               ║      ║ │  2) updatePreviewAsync      │ ┐ ──║───→│ renderers             │ ┐ ────→│ DOM elements          │ ┐
// ║                              ║      ║ │  5) applyTransform          │ │   ║    └───────────────────────┘ │ ┐    └───────────────────────┘ │ ┐
// ║                              ║      ║ └─────────────────────────────┘ │ ┐ ║      └───────────────────────┘ │      └───────────────────────┘ │
// ╚══════════════════════════════╝      ║   └─────────────────────────────┘ │ ║        └───────────────────────┘        └───────────────────────┘
//                                       ║     └─────────────────────────────┘ ║
//                                       ╚═════════════════════════════════════╝

/** Get a list of spreads */
function useSpreads() {
	const [spreads, setSpreads] = useState<readonly eXpress.core.Spread[]>([]);

	const editor = useEditor();

	// Listen for messages
	// --------------------
	useMessage(
		useCallback(
			function onSpreadsChangeDetectMessage(message) {
				// Don't check if interactive
				if (!message.isInteractive && eXpress.core.isSpreadCollectionChanged(message)) {
					const { definition } = editor;
					setSpreads(definition ? [...definition.spreads] : []); // clone spread array because it mutates
				}
			},
			[editor],
		),
	);

	return spreads;
}

export interface IAdornerImageSource {
	readonly uri: string;
	readonly size: eXpress.math.Vector;
}

export const SlideshowPreviewContainer = memo(() => {
	const zoneSelectionBoxRef = useRef<IZoneSelectionBoxRef>(null);

	const {
		editor,
		logger,
		defaultFontFamily,
		fontFamilyLoader,
		localizedContent,
		client,
		platform,
	} = useAppContext();

	const dashboard = useDashboardContext();

	const importer = useImporter();

	const { stack, setEnabled: setBottomMenuEnabled, gotoPath, currentPath } = useBottomMenu();

	const spreads = useSpreads();

	// Fetch labels for preview
	const [[loadingText]] = useLabels([labels.loadingLabel]);

	const debugPreview = useFeatureFlagToggle("debugPreview");

	const previewContext = usePreviewContext();
	const { previewBox } = usePreviewBox();
	const selectPhotosDialogAsync = useImportPhotosDialog();

	const { slideShowPreviewRef, previewOverlayRef, getZonesAtPoint, mutable, resetZoom } =
		previewContext;

	const spreadDirtyChecker = useSpreadDirtyChecker(() => {
		// Remark:
		// - Called synchronous, be careful to use react state
		// - Should we only rerender interactive messages sync and non-interactive still async (with react state)?

		// At the moment, we only rerender the one spread in view
		const slideShow = slideShowPreviewRef.current?.slideShow;
		if (!slideShow) return;

		// Used the selected spread from the slideshow, not from react state (sync callback)
		const selectedSpreadItem = slideShow.items[slideShow.index];

		// Because this message handler is sync before a react update triggers an update of slideShowItems,
		// the slideShowItem could be temporary outdated
		if (!selectedSpreadItem?.spread?.hasOwner()) return;

		updatePreview(selectedSpreadItem);
	});

	const [photoPreprocessProgress] = usePhotoPreprocessProgress();
	const isPreprocessing = !!photoPreprocessProgress;
	const selectedZone = useSelectedZone();
	const selectedPage = useLogicalSelectedPage();
	const selectedSpread = useSelectedSpread();
	const [isPreviewMode] = useIsPreviewMode();
	const isTextEditMode = useIsTextEditMode();
	const [isTransformMode, setIsTransformMode] = useIsTransformMode();

	const [isPhotoSwapping] = usePhotoSwapping(undefined);
	const [isZoneMoving] = useZoneMove();
	const [isPhotoPanning] = usePhotoPanning({});

	/**
	 * Function to update the spread rendering
	 * - Error handling included
	 * - When spread is not dirty it will not re-render
	 */
	const updatePreview = useCallback(
		function updatePreview(item: ISpreadSlideShowItem) {
			if (!item) return;
			const { spread } = item;

			// Don't start rendering of dangling spreads, they be skipped
			if (!spread.hasOwner()) {
				logger.warn(
					`updatePreview() requested for a dangling spread: ${spread?.toString()}`,
				);
				return;
			}

			// If spread is not changed since last render, don't rerender
			if (!spreadDirtyChecker.isDirty(spread)) return;
			spreadDirtyChecker.clearDirty(spread);

			// Load item
			item.updatePreviewAsync(isPreviewMode, isTransformMode ? selectedZone : undefined)
				.then(() => {
					// Show overlay divs from last requested render
					previewOverlayRef.current?.hideSpread(spread, item.isLoading);
				})
				.catch((e: unknown) => {
					client.logErrorAsync(e, false);

					// Hide overlay divs on error
					previewOverlayRef.current?.hideSpread(spread, true);
				});
		},
		[
			client,
			spreadDirtyChecker,
			logger,
			previewOverlayRef,
			isPreviewMode,
			isTransformMode,
			selectedZone,
		],
	);

	/** Item is in view */
	const showPreview = useCallback(
		function showPreview(item: ISpreadSlideShowItem) {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { spread } = item;

			// Don't start rendering of dangling spreads, they be skipped
			if (!spread.hasOwner()) {
				logger.warn(`showPreview() requested for a dangling spread: ${spread.toString()}`);
				return;
			}

			// If spread is not changed since last render, don't rerender
			if (!spreadDirtyChecker.isDirty(spread)) return;
			spreadDirtyChecker.clearDirty(spread);

			item.showPreviewAsync(isPreviewMode, isTransformMode ? selectedZone : undefined)
				.then(() => {
					// Show overlay divs from last requested render
					previewOverlayRef.current?.hideSpread(spread, item.isLoading);
				})
				.catch((e: unknown) => {
					client.logErrorAsync(e, false);

					// Hide overlay divs on error
					previewOverlayRef.current?.hideSpread(spread, true);
				});
		},
		// HACK: Excluded selectedZone, otherwise the preview became incorrectly zoomed, needs investigation...
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			client,
			logger,
			previewOverlayRef,
			slideShowPreviewRef,
			spreadDirtyChecker,
			isPreviewMode,
			isTransformMode,
		],
	);

	/** Item is not in view anymore */
	const hidePreview = useCallback(
		function hidePreview(item: ISpreadSlideShowItem) {
			const { spread } = item;

			// Set as dirty, so show/update are executed the next time
			spreadDirtyChecker.setDirty(spread);

			item.hidePreview();
		},
		[spreadDirtyChecker],
	);

	// Initialize slideShow (should be executed once)
	// ---------------------
	useEffect(
		function initSlideShow() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			const unsubscribeOnTransformChange = slideShow.onTransformChange.subscribe(
				function onTransformChange({ transforms }) {
					slideShow.setTransforms({
						slideshowViewIndexTransform: transforms.slideshowViewIndex.transform,
						slideshowViewRegionTransform: transforms.slideshowViewRegion.transform,
					});
				},
			);

			/** Update Slideshow item onItemTransformChange */
			const unsubscribeOnItemTransformChange = slideShow.onItemTransformChange.subscribe(
				function onItemTransformChange({ item, transforms }) {
					item.applyTransform(transforms);
				},
			);

			/**
			 * Set default viewBox if preview size changes
			 * Adorner loads asynchronious and triggers a update
			 * When adorner has a minimal adorner, it needs to be used by default
			 */
			const unsubscribeOnItemSizeChange = slideShow.onItemSizeChange.subscribe(
				function onItemSizeChange({ item, index }) {
					// Update the viewbox
					// The viewbox is for the selected item,
					// so only reset if the size of the current index is changed, not for prerendered items
					if (index === slideShow.index) {
						resetZoom(item.spread);
					}
				},
			);

			// Create object that manages the DOM elements
			return () => {
				unsubscribeOnItemSizeChange();
				unsubscribeOnItemTransformChange();
				unsubscribeOnTransformChange();
			};
		},
		[slideShowPreviewRef, resetZoom],
	);

	// Debug flag
	useEffect(() => {
		const slideShow = slideShowPreviewRef.current?.slideShow;
		if (!slideShow) return;

		// slideShow.debug = debugPreview;
		slideShow.showDebugBoxes = debugPreview;
	}, [debugPreview, slideShowPreviewRef]);

	// Preload spreads not in view
	// ----------------------------
	useSpreadUnloadingPreloading(slideShowPreviewRef, showPreview, hidePreview);

	// Shared data between rerenders (for caching)
	const createAdornerImageAsync = useAdornerImageCreator();

	const smartFilterLoader = useSmartFilterLoader();

	// Update Spread Previews (each spread object has a preview)
	// -----------------------
	useEffect(
		function updateSpreadPreviews() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const newSlideShowItems = new Map<eXpress.core.Spread, ISpreadSlideShowItem>();

			const showAdorners = (editor.definition?.product.productAdorners?.size ?? 0) > 0;
			// Create/reuse spreads previews
			spreads.forEach((spread) => {
				const reuseItem = mutable.slideShowItems.get(spread);
				if (reuseItem) {
					newSlideShowItems.set(spread, reuseItem);
					return;
				}

				// Get approximation of render size by fitting spread in portal rect (without adorner, margin, ...)
				const portalSize = previewContext.slideShowPreviewRef.current?.slideShow.portalSize;

				const maxSpreadRenderSize = portalSize
					? eXpress.math.vector(spread).fit(eXpress.math.vector(portalSize))
					: undefined;

				// The rendering currently creates square canvases, so we only need the max side
				let maxSpreadRenderSide = Math.max(
					maxSpreadRenderSize?.width ?? 0,
					maxSpreadRenderSize?.height ?? 0,
				);

				// Allow sharp zoom until 3x
				maxSpreadRenderSide *= 3;

				// Make sure canvas size does not exceed platform limits
				const maxCanvasSide = Math.min(
					platform.canvas.getMaxCanvasSide(platform.browserInfo),
					4096, // Add custom maximum canvas limit = 16MP
				);
				maxSpreadRenderSide = Math.min(maxSpreadRenderSide, maxCanvasSide);

				// Compensate for devicePixelRatio that the rendering will do
				const dpr = globalThis.devicePixelRatio || 1;
				maxSpreadRenderSide /= dpr;

				const item = createSpreadSlideShowItem(
					slideShow,
					spread,
					{
						renderingLoadingText: loadingText, // Not yet needed, when uploading partner Photos
						showDebugLayers: debugPreview,
						showAdorners,
						useSvgLayer: true,
						maxRenderSizeInPx: maxSpreadRenderSide,
					},
					{
						editor,
						logger,
						defaultFontFamily,
						fontFamilyLoader,
						localizedContent,
						client,
						platform,
						createAdornerImageAsync,
						smartFilterLoader,
					},
				);

				newSlideShowItems.set(spread, item);
			});

			// Removed items
			slideShow.items.forEach((item) => {
				// Mark removed spreads again as dirty, so they are rerendered the next time
				spreadDirtyChecker.setDirty(item.spread);

				// Remove from DOM
				if (!newSlideShowItems.has(item.spread)) {
					item.dispose(); // dispose will also remove it from the DOM
				}
			});

			// Add the new items to the slideshow
			mutable.slideShowItems = newSlideShowItems;
			slideShow.items = Array.from(newSlideShowItems.values());

			// Layout items
			slideShow.items.forEach((item) => {
				// We pass the DIP size of the spread as initial size.
				// The slideShow works with relative sizes, so this is okay.
				// When an adorner is present, use its DIP size instead
				const adorner = item.spread.getAdorner();
				const dipSize = adorner?.image ?? item.spread; // Remark: should we pass adorner size if we render no adorner?
				slideShow.setItemSize(item, dipSize);
			});
		},
		[
			spreads,
			debugPreview,
			client,
			defaultFontFamily,
			editor,
			fontFamilyLoader,
			platform,
			loadingText,
			localizedContent,
			logger,
			mutable,
			createAdornerImageAsync,
			slideShowPreviewRef,
			spreadDirtyChecker,
			smartFilterLoader,
			previewContext.slideShowPreviewRef,
		],
	);

	// Bring the selected spread into view
	// --------------------------------
	useEffect(
		function bringSelectedSpreadIntoView() {
			if (!selectedSpread) return;
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { index } = selectedSpread;
			const selectedSpreadItem = slideShow.items[index];
			eXpress.utils.assert(
				selectedSpreadItem?.spread === selectedSpread,
				"The slideshow spreads are not in sync with the definition spreads",
			);

			slideShow.index = index;

			resetZoom(selectedSpread);
		},
		[editor, resetZoom, selectedSpread, slideShowPreviewRef],
	);

	// If constellation changes, the current spread can be moved (example: the cover is removed for a constellation)
	// This should change the selected index in the slideshow to the new spread index position
	useMessageToState({
		name: "keep-selected-spread-into-view",
		filterMessage: (message) =>
			message instanceof eXpress.core.SpreadMovedMessage ||
			eXpress.core.isSelectedConstellationChanged(message),
		getState: useCallback(() => {
			if (!selectedSpread) return;
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { index } = selectedSpread;
			if (index !== slideShow.index) {
				slideShow.index = index;
			}
		}, [selectedSpread, slideShowPreviewRef]),
	});

	// Update slideshow item size (Normally when adorner changes)
	useMessageToState({
		name: "update-slideshow-item-size",
		// Product options can change the adorner without updating the spread
		filterMessage: eXpress.core.isProductOptionChanged,
		getState: useCallback(
			(editor2) => {
				const slideShow = slideShowPreviewRef.current?.slideShow;
				if (!slideShow) return [];

				// Another Product Option changes the adorner => set the new adorner size as slideshow item size
				slideShow.items.forEach((item) => {
					// Because this message handler is sync before a react update triggers an update of slideShowItems,
					// the slideShowItem could be temporary outdated
					if (!item.spread.hasOwner()) return;

					// We pass the DIP size of the spread as initial size.
					// The slideShow works with relative sizes, so this is okay.
					const adorner = item.spread.getAdorner();
					const dipSize = adorner?.image ?? item.spread; // Remark: should we pass adorner size if we render no adorner?
					slideShow.setItemSize(item, dipSize);
				});

				// Returned a value so useMessageToState can detect other cases where adorners change
				return editor2.definition?.product.productAdorners;
			},
			[slideShowPreviewRef],
		),
		comparer: jsonComparer,
	});

	// Update preview overlay
	// -----------------------
	usePreviewOverlay(
		slideShowPreviewRef,
		previewOverlayRef,
		selectedZone,
		selectedSpread,
		mutable,
	);

	// Gestures
	const dragImageRef = useRef<IDragImageRef>(null);
	useBindSlideshowGestures(dragImageRef);

	const { openZoneMapAsync } = useMapZones();

	// ZoneSelectionBox logic
	// -----------------------
	// Update the state when the allowed selection box handlers change
	// Example: when moving/resizing/other zone type is selected
	const [zoneResizingHandle, setZoneResizingHandle] = useState(ZoneSelectionBoxHandlers.None);
	const allowedZoneHandlers = useMemo(
		function getZoneSelectionBoxHandlers() {
			if (isTextEditMode) return ZoneSelectionBoxHandlers.None;

			// Resize
			if (zoneResizingHandle) return zoneResizingHandle;

			// Hide during Transform mode
			if (isTransformMode) return ZoneSelectionBoxHandlers.None;

			// Resize allowed?
			if (dashboard.canResizeZone(selectedZone)) {
				// Customize handlers (Example: symbols only allow uniform scale; photo zone with frames)
				if (
					eXpress.core.isPhotoZone(selectedZone) &&
					(!!selectedZone.effect || selectedZone.isZoneForPhotoSourceType("qr"))
				)
					return ZoneSelectionBoxHandlers.Corners;

				// Symbol zones only allow uniform scaling
				if (eXpress.core.isSymbolZone(selectedZone)) {
					return ZoneSelectionBoxHandlers.Corners;
				}

				// All (depending on available space)
				return ZoneSelectionBoxHandlers.All;
			}

			return ZoneSelectionBoxHandlers.None;
		},
		[isTextEditMode, zoneResizingHandle, isTransformMode, dashboard, selectedZone],
	);

	useZoneSelectionBox({
		slideShowPreviewRef,
		zoneSelectionBox: zoneSelectionBoxRef.current,
		previewContext,
		allowedZoneHandlers,
		onResize: setZoneResizingHandle,
	});

	// show guidelines
	const guidelineOverlayRef = useRef<IGuidelineOverlayRef>(null);
	const [, useLines] = useGuidelineOverlay();
	if (guidelineOverlayRef.current) {
		useLines.start(
			guidelineOverlayRef.current?.el,
			guidelineOverlayRef.current?.drawGuidelines,
			guidelineOverlayRef.current?.removeGuidelines,
			zoneResizingHandle,
		);
	}
	// Rerender selected spread
	// -------------------------
	useEffect(
		function rerenderDirtySpreads() {
			if (!selectedSpread) return;
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { index } = selectedSpread;
			const selectedSpreadItem = slideShow.items[index];

			// The slideshow spreads are not in sync with the definition spreads (see isSpreadCollectionChanged?)
			if (selectedSpreadItem?.spread !== selectedSpread) return;

			// At the moment, we only rerender the one spread in view
			// When we select a spread, we will rerender at here.
			updatePreview(selectedSpreadItem);
		},
		[selectedSpread, slideShowPreviewRef, updatePreview, isTransformMode],
	);

	// Update render if new spread comes into view
	// --------------------------------------------
	useEffect(
		function loadPreviewSlideShowChange() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			/** Get notified if another item is visible */
			return slideShow.onViewChange.subscribe(function onViewChangeShowSpread({ index }) {
				const item = slideShow.items[index];
				if (!item) return;

				// Make sure item that comes into view is not dirty
				showPreview(item);
			});
		},
		[client, slideShowPreviewRef, showPreview],
	);

	// Update slideshow when available preview space changes
	// ------------------------------------------------------
	useEffect(
		function updateWhenPreviewAreaChanged() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			slideShow.updateSlideShow(() => {
				slideShow.portalSize = eXpress.math.vector(
					slideShow.el.clientWidth,
					slideShow.el.clientHeight,
				); // didn't listen to resize/orientationchange events, because I don't think it is needed yet
				if (!slideShow.portalSize.width) debugger;
				if (!previewBox.width) return; // Still default size of 0
				const portalPaddings = eXpress.math.margins(
					previewBox.left,
					previewBox.top,
					Math.ceil(slideShow.portalSize.width - previewBox.left - previewBox.width),
					Math.ceil(slideShow.portalSize.height - previewBox.top - previewBox.height),
				);
				slideShow.portalPaddings = portalPaddings;
			});
		},
		[editor, slideShowPreviewRef, previewBox],
	);

	const { createErrorHandler } = useErrorHandling();

	const { busyBlocker } = useBusyBlockerContext();

	/**
	 * When OSK is open on iOS, the page is partly scrolled outside the viewport
	 * We disabled the native browser gestures in CRE-1634 but
	 * would now like the allow the user to scroll up and down when in text edit mode
	 * HACK: We query the .app element instead of using a ref to app element
	 */
	useEffect(() => {
		if (!platform.browserInfo.isIos()) return; // Not really needed, on Android no panning possible
		const appEl = document.querySelector<HTMLDivElement>(".app");
		if (!appEl) return;
		const isEditingText = stack.some((item) => item.id === BottomMenuIds.TextValue);
		appEl.style.touchAction = isEditingText ? "pan-y" : "";
	}, [platform, stack]);

	/** Select a new zone and update the bottom menu for it */
	const selectZone = useCallback(
		function selectZone(newZone: eXpress.core.IZone | undefined) {
			// Check if we need to open a new bottom menu for this zone
			const [newPath, isBottomMenuChanged] = updateBottomMenuWhenClicked(
				newZone,
				selectedZone,
				currentPath,
			);

			// Select Zone
			if (isBottomMenuChanged) {
				gotoPath(newPath);

				// Because we set the bottom menu to a specific menu in the line above,
				// we need to temporary disable the automatic bottom-menu update when zone selection is changed
				setBottomMenuEnabled(false);
				editor.selection.setCurrentZoneAndBroadcast(newZone);
				setBottomMenuEnabled(true);
			} else {
				editor.selection.setCurrentZoneAndBroadcast(newZone);
			}
		},
		[currentPath, editor.selection, gotoPath, selectedZone, setBottomMenuEnabled],
	);

	/** Select a new zone and update the bottom menu for it */
	const addToZone = useCallback(
		function addPhotoToZone(clickedZone: eXpress.core.IZone | undefined) {
			const photoZone = eXpress.core.isPhotoZone(clickedZone) ? clickedZone : undefined;
			if (photoZone) {
				const isSameZoneSelected = clickedZone === selectedZone;
				if (!isSameZoneSelected) return;
				if (isPreprocessing) return;
				if (!dashboard.canApplyPhotoToZone(photoZone)) return;

				const photoSourceType =
					photoZone.photoSourceType ?? photoZone.defaultPhotoSourceType;
				switch (photoSourceType) {
					case "photo": {
						// Import Photo on empty photozone
						if (!photoZone.isEmpty) return;

						const isPhotoSelectionOpen = stack.find((s) => s.id === "photo-selection");
						const shouldStartImport = isPhotoSelectionOpen;

						if (shouldStartImport) {
							busyBlocker
								.during(
									selectPhotosDialogAsync().then((selectedPhotos) => {
										if (selectedPhotos && selectedPhotos.length > 0) {
											// Add first to zone
											if (
												dashboard.canApplyPhotoToZone(
													photoZone,
													selectedPhotos[0],
												)
											) {
												// Async: PhotoZone could already be removed?
												dashboard.applyPhotoToZone(
													photoZone,
													selectedPhotos[0],
												);
											}

											// Add all to photo selection
											editor.addToPhotoSelection(selectedPhotos, (photos) => {
												importer?.addToQueue(photos);
											});
										}
									}),
								)
								.catch(createErrorHandler());
						}
						break;
					}
					case "map": {
						openZoneMapAsync?.().catch(createErrorHandler());
						break;
					}
					case "qr": {
						if (clickedZone?.isEmpty) {
							const warning = new eXpress.GeneralWarning(
								"EmptyQrZoneClickedWarning",
								"slideshow-preview-container",
								"We don't expect empty QR zones",
							);
							createErrorHandler()(warning);
						}

						gotoPath(BottomMenuPaths.QrEdit);
						break;
					}
					default: {
						eXpress.utils.exhaustiveFail(photoSourceType, true);
						break;
					}
				}
			}
		},
		[
			busyBlocker,
			createErrorHandler,
			dashboard,
			editor,
			importer,
			selectPhotosDialogAsync,
			isPreprocessing,
			selectedZone,
			stack,
			openZoneMapAsync,
			gotoPath,
		],
	);

	const onDoubleClick = useCallback(
		function slideShowDoubleClicked({ pageX: x, pageY: y }: MouseEvent) {
			// Don't do anything in Preview Mode
			if (isPreviewMode) return;

			const clickedZone = getZonesAtPoint(
				{ x, y },
				(zone) =>
					eXpress.core.isAnyTextZone(zone) ||
					(eXpress.core.isPhotoZone(zone) && dashboard.canApplyPhotoToZone(zone)) ||
					(eXpress.core.isSymbolZone(zone) &&
						(dashboard.editor.isSymbolEditingEnabled() ?? false)),
			).last();

			if (isTransformMode) {
				// Disable transform Mode
				setIsTransformMode(false);
				const menuPath = getDefaultPathForZone(
					BottomMenuPaths.PhotoTransform,
					selectedZone,
					selectedPage,
					{
						main: dashboard,
					},
					false,
				);
				gotoPath(menuPath); // There is still a photozone selected
			} else if (
				clickedZone &&
				!clickedZone.isEmpty &&
				isPhotoTransformSupported(clickedZone, dashboard)
			) {
				selectZone(clickedZone);

				if (eXpress.core.isPhotoZone(clickedZone) && clickedZone.photoSourceType === "qr") {
					addToZone(clickedZone);
				} else {
					setIsTransformMode(true);
				}
			} else {
				// Add Photo on empty photozone, if needed
				addToZone(clickedZone);
				selectZone(clickedZone);
			}
		},
		[
			isPreviewMode,
			getZonesAtPoint,
			isTransformMode,
			addToZone,
			setIsTransformMode,
			gotoPath,
			selectedZone,
			selectedPage,
			dashboard,
			selectZone,
		],
	);

	// When product clicked
	// ---------------------
	const onClick = useCallback(
		function slideShowClicked({ pageX: x, pageY: y, detail }: React.MouseEvent) {
			// prevent from running twice on double click
			if (detail > 1) return;
			if (isPreviewMode) return;

			let clickedZone: eXpress.core.IZone | undefined = undefined;
			const possibleClickedZones = getZonesAtPoint(
				{ x, y },
				(zone) =>
					eXpress.core.isAnyTextZone(zone) ||
					(eXpress.core.isPhotoZone(zone) && dashboard.canApplyPhotoToZone(zone)) ||
					(eXpress.core.isSymbolZone(zone) &&
						(dashboard.editor.isSymbolEditingEnabled() ?? false)),
			).orderBy((z1, z2) => (z1.depth ?? 0) - (z2.depth ?? 0));

			// If the users taps the selected zone again,
			// select the next zone to allow cycling through overlapping zones (top zone first).
			if (selectedZone && possibleClickedZones.includes(selectedZone)) {
				// If the currently selected zone is empty, we should keep the zone selected.
				// Tapping again on an empty Text/PhotoZone should enable editing/adding a photo/map.
				if (selectedZone.isEmpty) {
					clickedZone = selectedZone;
				} else {
					const index = possibleClickedZones.indexOf(selectedZone);
					if (index >= possibleClickedZones.length - 1) {
						clickedZone = possibleClickedZones[0];
					} else {
						clickedZone = possibleClickedZones[index + 1];
					}
				}
			} else {
				// Normal case, just take the topmost zone
				clickedZone = possibleClickedZones.first();
			}

			// When we are in transform mode: click should only deselect when clicking something else than the current zone
			if (isTransformMode && clickedZone !== selectedZone) {
				setIsTransformMode(false);
				const menuPath = getDefaultPathForZone(
					BottomMenuPaths.PhotoTransform,
					selectedZone,
					selectedPage,
					{
						main: dashboard,
					},
					false,
				);
				gotoPath(menuPath); // There is still a photozone selected
				return;
			}
			if (isTransformMode && clickedZone === selectedZone) {
				// Do nothing
				return;
			}
			if (
				isTextEditMode &&
				clickedZone !== selectedZone &&
				clickedZone instanceof eXpress.core.TextZone
			) {
				addToZone(clickedZone);
				selectZone(clickedZone);
				return;
			}

			// Leave textEditMode when clicking anywhere outside the selected zone
			if (isTextEditMode && clickedZone !== selectedZone) {
				gotoPath(BottomMenuPaths.Text);
				return;
			}

			selectZone(clickedZone);
			addToZone(clickedZone);
		},
		[
			addToZone,
			getZonesAtPoint,
			selectZone,
			setIsTransformMode,
			gotoPath,
			dashboard,
			isPreviewMode,
			isTransformMode,
			selectedZone,
			selectedPage,
			isTextEditMode,
		],
	);

	const updateMouseCursor = useCallback(
		(x: number, y: number) => {
			// use inner function so we can use early exit to set cursor
			function getCursor() {
				// During actions
				if (isPhotoSwapping) return "grabbing";
				if (isZoneMoving) return "grabbing";
				if (isPhotoPanning) return "grabbing";

				const hoveringZones = getZonesAtPoint(
					{ x, y },
					(zone) =>
						eXpress.core.isAnyTextZone(zone) ||
						(eXpress.core.isPhotoZone(zone) && dashboard.canApplyPhotoToZone(zone)) ||
						(eXpress.core.isSymbolZone(zone) &&
							(dashboard.editor.isSymbolEditingEnabled() ?? false)),
				);

				// Take overlapping zones into account!
				const isHoveringSelectableZone = hoveringZones.some((zone) => zone.canSelect());
				const isHoveringSelectedZone =
					!!selectedZone && hoveringZones.includes(selectedZone);

				/** Not in some Editing/Transform mode*/
				const isDefaultMode = !isTextEditMode && !isTransformMode && !isPreviewMode;

				// Hovering a selectable non-selected zone
				// In Transform mode or Text Edit Mode, don't mark it as clickable, it will exit the mode
				if (!isHoveringSelectedZone && isHoveringSelectableZone && isDefaultMode)
					return "pointer";

				// Hovering an empty selected zone (click to fill)
				if (isHoveringSelectedZone && selectedZone?.isEmpty && isDefaultMode)
					return "pointer";

				// Hovering a movable selected zone
				// In Text Edit mode, resize/move/rotate zone is not possible
				if (isHoveringSelectedZone && selectedZone?.canChangeTransform() && isDefaultMode)
					return "grab";

				return "default";
			}

			// Set cursor
			const style = slideShowPreviewRef.current?.el.style;
			if (!style) return;

			const cursor = getCursor();
			if (cursor) style.cursor = cursor;
		},
		[
			dashboard,
			getZonesAtPoint,
			isPhotoPanning,
			isPhotoSwapping,
			isPreviewMode,
			isTextEditMode,
			isTransformMode,
			isZoneMoving,
			selectedZone,
			slideShowPreviewRef,
		],
	);

	/**
	 * Keep last mouse position, used when cursor needs update without a mouse move \
	 * example:
	 * - Ctrl + Z to undo
	 * - click/select a zone without a mouse move
	 */
	const lastMousePositionRef = useRef({ x: 0, y: 0 });

	const onMouseMove = useCallback(
		function slideShowMouseMove({ pageX: x, pageY: y }: React.MouseEvent) {
			lastMousePositionRef.current = { x, y };
			updateMouseCursor(x, y);
		},
		[updateMouseCursor],
	);

	// Update mouse cursor also on state change, not only on mouse move
	// If one of the dependencies of updateMouseCursor changes, the cursor will be updated
	useEffect(() => {
		const { x = 0, y = 0 } = lastMousePositionRef.current || {};
		updateMouseCursor(x, y);
	}, [updateMouseCursor]);

	return (
		<>
			<SlideshowPreview
				ref={slideShowPreviewRef}
				onClick={onClick}
				onMouseMove={onMouseMove}
				onDoubleClick={onDoubleClick}
			/>
			<SpreadOverview />
			<PreviewOverlay ref={previewOverlayRef} />
			<PanCropOverlay />
			<GuidelineOverlay ref={guidelineOverlayRef} />
			<ZoneSelectionBox ref={zoneSelectionBoxRef} handles={allowedZoneHandlers} />
			<DragImage ref={dragImageRef} />
		</>
	);
});
SlideshowPreviewContainer.displayName = "SlideshowPreviewContainer";
